import React, { useState, useEffect, useCallback } from 'react';

const CAFBC4Presentation = ({ onComplete, currentSlide: externalSlide, onSlideChange }) => {
  const [currentSlide, setCurrentSlide] = useState(externalSlide || 0);

  useEffect(() => {
    if (externalSlide !== undefined) setCurrentSlide(externalSlide);
  }, [externalSlide]);

  const bgGradient = 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 30%, #FED7AA 70%, #FDBA74 100%)';
  const primaryColor = '#EA580C';
  const secondaryColor = '#C2410C';
  const accentColor = '#D97706';

  const slides = [
  // ============================================================
  // SLIDE 0 — TITRE
  // ============================================================
  { type: 'title', title: "Bloc 4", subtitle: "Contribuer au projet d'établissement ou de service", chapters: "5 chapitres • Introduction • Conclusion", icon: "BC4" },

  // ============================================================
  // SLIDE 1 — PRÉSENTATION DU BLOC
  // ============================================================
  { type: 'content', title: "Présentation du Bloc 4",
    body: "Le Bloc de compétences 4 du CAFERUIS porte sur la contribution du responsable d'unité au projet d'établissement ou de service.\n\nCe bloc aborde la capacité du cadre intermédiaire à mobiliser son expertise de terrain, à participer à l'évaluation du projet institutionnel, à concevoir un projet de service cohérent, à développer une veille professionnelle et à assurer la communication autour du projet.\n\nLe responsable d'unité joue un rôle stratégique d'interface entre les orientations de la direction et les réalités du terrain, contribuant ainsi à l'amélioration continue des dispositifs d'accompagnement.",
    highlight: "Ce bloc mobilise des compétences transversales : analyse stratégique, méthodologie de projet, communication institutionnelle et innovation sociale." },

  // ============================================================
  // SLIDE 2 — SOMMAIRE GÉNÉRAL
  // ============================================================
  { type: 'sommaire', title: "Sommaire du Bloc 4", chapters: [
      { num: "1", title: "Chapitre 1 — Apporter une expertise technique", targetSlide: 5 },
      { num: "2", title: "Chapitre 2 — Évaluer le projet d'établissement ou de service", targetSlide: 29 },
      { num: "3", title: "Chapitre 3 — Concevoir le projet de l'UIS en cohérence avec le projet d'établissement", targetSlide: 51 },
      { num: "4", title: "Chapitre 4 — Développer une veille professionnelle", targetSlide: 74 },
      { num: "5", title: "Chapitre 5 — Communiquer sur le projet d'établissement ou de service", targetSlide: 96 }
  ]},

  // ============================================================
  // SLIDE 3 — ENJEUX STRATÉGIQUES
  // ============================================================
  { type: 'content', title: "Les enjeux stratégiques du projet d'établissement",
    body: "Le projet d'établissement constitue le document fondateur de toute structure sociale ou médico-sociale. Il définit les missions, les valeurs et les orientations stratégiques qui guident l'action des professionnels.\n\nDans un secteur en constante évolution, le projet d'établissement doit intégrer les transformations des politiques publiques, les évolutions des besoins des publics et les exigences croissantes en matière de qualité et d'évaluation.",
    bullets: [
      "Cadre légal : obligation de rédiger un projet d'établissement (loi 2002-2)",
      "Durée : cycle de 5 ans avec évaluation régulière",
      "Dimensions : accompagnement des usagers, organisation, partenariats, ressources humaines",
      "Finalité : améliorer la qualité des services et répondre aux besoins du territoire"
    ] },

  // ============================================================
  // SLIDE 4 — RÔLE DU RESPONSABLE
  // ============================================================
  { type: 'content', title: "Le rôle du responsable d'unité dans la dynamique institutionnelle",
    body: "Le responsable d'unité occupe une position stratégique au sein de l'organisation. En tant que cadre intermédiaire, il assure le lien entre les orientations de la direction et les pratiques des équipes de terrain.",
    bullets: [
      "Expert de terrain : connaissance fine des publics, des pratiques et des difficultés",
      "Interface stratégique : traduction des orientations en actions opérationnelles",
      "Animateur : mobilisation des équipes autour du projet institutionnel",
      "Force de proposition : identification des besoins émergents et formulation de préconisations",
      "Communicateur : diffusion des valeurs et des objectifs du projet auprès des parties prenantes"
    ],
    highlight: "Le responsable d'unité ne se limite pas à l'exécution : il contribue activement à la réflexion et à la construction stratégique de l'établissement." },

  // ============================================================
  // CHAPITRE 1 — APPORTER UNE EXPERTISE TECHNIQUE
  // ============================================================
  // SLIDE 5 — SECTION
  { type: 'section', title: "Chapitre 1", subtitle: "Apporter une expertise technique", description: "Le rôle d'expert du responsable d'unité, la veille technique, le diagnostic des besoins et le conseil auprès de la direction." },

  // SLIDE 6 — INTRO CHAP 1
  { type: 'content', title: "Introduction — Chapitre 1",
    body: "Dans le secteur social et médico-social, les établissements évoluent dans un environnement en constante transformation. Les besoins des publics accompagnés se diversifient, les politiques publiques se réforment régulièrement et les organisations doivent adapter leurs pratiques.\n\nLe responsable d'unité contribue à la réflexion stratégique de l'établissement en apportant son expertise technique et sa connaissance du terrain. Il joue un rôle d'interface entre les réalités du terrain et les orientations stratégiques de l'établissement.",
    bullets: [
      "Analyse des besoins des publics accompagnés",
      "Compréhension des évolutions du territoire",
      "Identification des problématiques émergentes",
      "Formulation de propositions d'amélioration ou d'innovation"
    ] },

  // SLIDE 7 — OBJECTIFS CHAP 1
  { type: 'content', title: "Objectifs pédagogiques — Chapitre 1",
    body: "À l'issue de ce module, le professionnel doit être capable de :",
    bullets: [
      "Mobiliser son expertise technique dans la réflexion stratégique de l'établissement",
      "Analyser les besoins des publics accompagnés et du territoire",
      "Identifier les problématiques émergentes dans le secteur social et médico-social",
      "Formuler des préconisations adaptées à la direction"
    ] },

  // SLIDE 8 — SOMMAIRE CHAP 1
  { type: 'grid', title: "Sommaire — Chapitre 1 : Apporter une expertise technique", items: [
    { title: "1. Le rôle d'expert", desc: "Définition de l'expertise, posture d'analyse et prise de recul professionnelle" },
    { title: "2. La veille technique", desc: "Surveillance des évolutions : vieillissement, handicap, protection de l'enfance" },
    { title: "3. Le diagnostic des besoins", desc: "Analyse des besoins non couverts, situations complexes, outils du diagnostic" },
    { title: "4. Le conseil auprès de la direction", desc: "Préconisations stratégiques, contribution au projet d'établissement" }
  ]},

  // SLIDE 9 — SOUS-SECTION 1
  { type: 'section', title: "1. Le rôle d'expert", subtitle: "du responsable d'unité", description: "Définition de l'expertise professionnelle et posture d'analyse." },

  // SLIDE 10 — Définition expertise
  { type: 'content', title: "Définition de l'expertise professionnelle",
    body: "L'expertise professionnelle peut être définie comme la capacité à mobiliser des connaissances, des compétences et une expérience de terrain afin d'analyser une situation complexe et de proposer des solutions adaptées.\n\nDans le secteur social et médico-social, l'expertise du responsable d'unité repose sur plusieurs dimensions :",
    bullets: [
      "La connaissance approfondie des publics accompagnés",
      "La compréhension des politiques sociales et de leur évolution",
      "La maîtrise des pratiques professionnelles d'intervention",
      "L'analyse des évolutions du secteur et des besoins émergents"
    ],
    highlight: "Cette expertise permet d'éclairer les décisions stratégiques de l'établissement et de proposer des réponses adaptées aux réalités du terrain." },

  // SLIDE 11 — Posture d'expert
  { type: 'grid', title: "La posture d'expert : trois compétences clés", items: [
    { title: "L'analyse des situations", desc: "Observer et analyser les situations rencontrées dans le service afin d'identifier les problématiques émergentes et les tendances." },
    { title: "La prise de recul", desc: "Développer une capacité à s'extraire des pratiques quotidiennes pour identifier les enjeux plus larges et les dynamiques structurelles." },
    { title: "La capacité d'argumentation", desc: "Formuler des analyses et des propositions structurées, fondées sur des éléments objectifs, auprès de la direction et des partenaires." }
  ]},

  // SLIDE 12 — SOUS-SECTION 2
  { type: 'section', title: "2. La veille technique", subtitle: "sur les publics accompagnés", description: "Anticipation des transformations du secteur et adaptation des pratiques." },

  // SLIDE 13 — Définition veille technique
  { type: 'content', title: "La veille technique : définition et enjeux",
    body: "La veille technique consiste à surveiller l'évolution des connaissances, des pratiques et des politiques publiques dans un domaine professionnel. Elle permet d'anticiper les transformations du secteur et d'adapter les pratiques professionnelles.\n\nDans le secteur social et médico-social, la veille peut concerner :",
    bullets: [
      "Les évolutions législatives et réglementaires (lois, décrets, circulaires)",
      "Les nouvelles pratiques d'accompagnement validées par la recherche",
      "Les innovations sociales et organisationnelles",
      "Les transformations des publics accompagnés et de leurs besoins"
    ] },

  // SLIDE 14 — Thématiques de veille
  { type: 'content', title: "Les thématiques de veille dans le secteur social",
    body: "Le responsable d'unité doit être attentif aux évolutions majeures qui transforment le secteur social et médico-social. Ces évolutions modifient les besoins des publics, les modes d'intervention et les politiques publiques.",
    highlight: "Trois domaines sont particulièrement impactés par les transformations en cours : le vieillissement de la population, les situations de handicap et la protection de l'enfance." },

  // SLIDE 15 — Vieillissement
  { type: 'content', title: "Le vieillissement de la population",
    body: "Le vieillissement démographique constitue un enjeu majeur pour les politiques sociales. La France compte de plus en plus de personnes âgées dépendantes, ce qui nécessite une adaptation profonde des dispositifs d'accompagnement.\n\nCe phénomène implique notamment :",
    bullets: [
      "L'adaptation des dispositifs d'accompagnement (maintien à domicile, EHPAD, accueil de jour)",
      "Le développement de nouvelles formes de prise en charge (habitat inclusif, solutions intermédiaires)",
      "La coordination renforcée entre les acteurs sanitaires et sociaux",
      "La prévention de la perte d'autonomie et le soutien aux aidants familiaux"
    ] },

  // SLIDE 16 — Handicap
  { type: 'content', title: "Les situations de handicap",
    body: "Les politiques publiques évoluent vers une approche centrée sur l'inclusion et la participation des personnes en situation de handicap, conformément à la loi du 11 février 2005 et à la Convention des Nations Unies relative aux droits des personnes handicapées.\n\nCette évolution implique :",
    bullets: [
      "L'adaptation des pratiques professionnelles vers l'autodétermination",
      "Le développement de dispositifs inclusifs (habitat inclusif, emploi accompagné)",
      "La coordination entre secteur médico-social, éducation et emploi",
      "La mise en œuvre effective du parcours individualisé et du projet de vie"
    ] },

  // SLIDE 17 — Protection enfance
  { type: 'content', title: "La protection de l'enfance",
    body: "Le secteur de la protection de l'enfance connaît des transformations importantes, marquées par les lois de 2007, 2016 et 2022 qui renforcent la place de l'enfant au cœur des dispositifs.\n\nLes professionnels doivent notamment faire face à :",
    bullets: [
      "L'augmentation de certaines situations complexes (psychotraumatismes, troubles du comportement)",
      "La nécessité de renforcer les actions de prévention précoce",
      "La coordination entre les différents acteurs du territoire (ASE, justice, santé, éducation)",
      "L'évolution des modalités d'accueil (diversification des réponses, parrainage, tiers digne de confiance)"
    ] },

  // SLIDE 18 — Sources d'information
  { type: 'grid', title: "Les sources d'information pour la veille technique", items: [
    { title: "Publications institutionnelles", desc: "Rapports de l'IGAS, DREES, CNSA, ONED/ONPE, observatoires territoriaux" },
    { title: "Rapports d'experts", desc: "Études universitaires, rapports parlementaires, avis du CESE, études de la FAS" },
    { title: "Recommandations de bonnes pratiques", desc: "Guides de la HAS et de l'ANESM, référentiels sectoriels, protocoles validés" },
    { title: "Réseaux professionnels", desc: "Associations de cadres, fédérations (FEHAP, NEXEM, UNIOPSS), colloques et journées d'étude" }
  ]},

  // SLIDE 19 — SOUS-SECTION 3
  { type: 'section', title: "3. Le diagnostic des besoins", subtitle: "Analyse du terrain et identification des lacunes", description: "Comprendre les besoins non couverts et les situations complexes du territoire." },

  // SLIDE 20 — Définition diagnostic
  { type: 'content', title: "Définition du diagnostic",
    body: "Le diagnostic correspond à une démarche d'analyse visant à comprendre une situation afin d'identifier les besoins et les problématiques. C'est la première étape de toute démarche de projet ou d'amélioration.\n\nDans le secteur social, le diagnostic peut porter sur :",
    bullets: [
      "Les besoins des publics accompagnés (nature, intensité, évolution)",
      "Les ressources disponibles sur le territoire (offre de services, partenariats)",
      "Les éventuelles lacunes dans les dispositifs existants",
      "Les attentes exprimées par les usagers, les familles et les professionnels"
    ] },

  // SLIDE 21 — Besoins non couverts
  { type: 'content', title: "L'analyse des besoins non couverts",
    body: "L'analyse des besoins non couverts consiste à identifier les situations pour lesquelles les dispositifs existants ne répondent pas suffisamment aux besoins des usagers. Le responsable d'unité, par sa proximité avec le terrain, est en première ligne pour repérer ces carences.",
    bullets: [
      "Publics spécifiques mal desservis (personnes vieillissantes handicapées, jeunes en errance)",
      "Problématiques émergentes (addictions numériques, isolement social post-COVID)",
      "Difficultés d'accès aux dispositifs existants (barrières géographiques, administratives, culturelles)",
      "Inadéquation entre l'offre de services et les besoins réels du territoire"
    ],
    highlight: "Le repérage de ces besoins non couverts constitue un levier essentiel pour l'adaptation des dispositifs et l'innovation sociale." },

  // SLIDE 22 — Cas complexes
  { type: 'content', title: "Les situations complexes",
    body: "Certaines situations rencontrées dans le secteur social sont particulièrement complexes et nécessitent une analyse approfondie. Elles se caractérisent souvent par :",
    bullets: [
      "La pluralité des problématiques (sociales, médicales, psychologiques, juridiques)",
      "L'implication de plusieurs institutions ou partenaires",
      "Des situations familiales ou personnelles particulièrement difficiles",
      "L'échec ou l'insuffisance des réponses classiques"
    ],
    highlight: "L'analyse de ces situations permet d'identifier les limites des dispositifs existants et de proposer des améliorations structurelles ou organisationnelles." },

  // SLIDE 23 — Outils du diagnostic
  { type: 'grid', title: "Les outils du diagnostic", items: [
    { title: "Données statistiques", desc: "Indicateurs démographiques, données d'activité, taux d'occupation, profils des usagers accompagnés" },
    { title: "Observations professionnelles", desc: "Remontées de terrain, cahiers d'incidents, observations cliniques, groupes d'analyse de pratiques" },
    { title: "Échanges partenariaux", desc: "Réunions de réseau, commissions territoriales, diagnostics partagés avec les partenaires" },
    { title: "Retours des usagers", desc: "Enquêtes de satisfaction, entretiens individuels, expression au CVS, cahier de doléances" }
  ]},

  // SLIDE 24 — SOUS-SECTION 4
  { type: 'section', title: "4. Le rôle de conseil", subtitle: "auprès de la direction", description: "Formulation de préconisations et contribution au projet d'établissement." },

  // SLIDE 25 — Conseil stratégique
  { type: 'content', title: "Le conseil stratégique",
    body: "Le responsable d'unité peut être amené à formuler des propositions auprès de la direction afin d'améliorer le fonctionnement du service ou de développer de nouvelles actions.\n\nCe rôle de conseil repose sur :",
    bullets: [
      "L'analyse rigoureuse des besoins identifiés sur le terrain",
      "L'identification des enjeux stratégiques pour l'établissement",
      "La formulation de recommandations argumentées et réalistes",
      "La prise en compte des contraintes organisationnelles et financières"
    ] },

  // SLIDE 26 — Préconisations
  { type: 'content', title: "La formulation de préconisations",
    body: "Les préconisations correspondent aux propositions formulées afin d'améliorer les pratiques ou les dispositifs existants. Elles doivent être fondées sur des éléments objectifs et structurées de manière claire.",
    bullets: [
      "Évolution des pratiques professionnelles (nouveaux protocoles, formations ciblées)",
      "Création de nouveaux dispositifs (ateliers, groupes de parole, partenariats)",
      "Adaptation de l'organisation du service (plannings, fonctionnement, coordination)",
      "Optimisation des ressources existantes (mutualisation, redéploiement)"
    ],
    highlight: "Des préconisations bien argumentées renforcent la crédibilité du responsable d'unité et facilitent la prise de décision par la direction." },

  // SLIDE 27 — Contribution au projet
  { type: 'content', title: "La contribution au projet d'établissement",
    body: "Le projet d'établissement constitue un document stratégique qui définit les orientations et les objectifs de la structure. Le responsable d'unité contribue activement à son élaboration en :",
    bullets: [
      "Partageant son analyse du terrain et des besoins des publics",
      "Identifiant les forces et les faiblesses de l'organisation actuelle",
      "Proposant des pistes d'amélioration concrètes et réalistes",
      "Participant aux groupes de travail et aux instances de réflexion"
    ],
    highlight: "Cette contribution permet de renforcer la cohérence entre les orientations stratégiques de l'établissement et les réalités du terrain." },

  // SLIDE 28 — Conclusion Chap 1
  { type: 'content', title: "Conclusion — Chapitre 1",
    body: "L'expertise technique constitue une compétence essentielle du responsable d'unité dans le cadre du CAFERUIS.\n\nElle permet de :",
    bullets: [
      "Analyser finement les besoins des publics accompagnés",
      "Anticiper les évolutions du secteur social et médico-social",
      "Contribuer activement à la réflexion stratégique de l'établissement",
      "Formuler des préconisations fondées sur l'analyse du terrain"
    ],
    highlight: "En mobilisant son expertise, le responsable d'unité participe à l'amélioration des dispositifs d'accompagnement et au développement des projets institutionnels." },

  // ============================================================
  // CHAPITRE 2 — ÉVALUER LE PROJET D'ÉTABLISSEMENT
  // ============================================================
  // SLIDE 29 — SECTION
  { type: 'section', title: "Chapitre 2", subtitle: "Évaluer le projet d'établissement ou de service", description: "Le cycle du projet, l'évaluation interne et externe, le référentiel HAS et la participation des acteurs." },

  // SLIDE 30 — INTRO CHAP 2
  { type: 'content', title: "Introduction — Chapitre 2",
    body: "Le projet d'établissement ou de service constitue un document stratégique fondamental dans les structures sociales et médico-sociales. Cependant, il ne peut être considéré comme un document figé : il doit faire l'objet d'une évaluation régulière.\n\nLe responsable d'unité joue un rôle important dans cette démarche en mobilisant son expertise du terrain et en facilitant la participation des professionnels, des usagers et des familles.\n\nL'évaluation s'inscrit dans le cadre des exigences réglementaires du secteur, notamment à travers les dispositifs d'évaluation interne et externe pilotés par la Haute Autorité de Santé (HAS)." },

  // SLIDE 31 — OBJECTIFS CHAP 2
  { type: 'content', title: "Objectifs pédagogiques — Chapitre 2",
    body: "À l'issue de ce module, le professionnel doit être capable de :",
    bullets: [
      "Comprendre le cycle de vie d'un projet d'établissement (5 ans)",
      "Participer activement à la démarche d'évaluation interne et externe",
      "Mobiliser les équipes dans l'analyse des pratiques professionnelles",
      "Favoriser la participation des usagers et des familles dans l'évaluation",
      "Utiliser les référentiels de la HAS pour structurer l'évaluation"
    ] },

  // SLIDE 32 — SOMMAIRE CHAP 2
  { type: 'grid', title: "Sommaire — Chapitre 2 : Évaluer le projet", items: [
    { title: "1. Le projet d'établissement", desc: "Définition, missions, objectifs et cadre réglementaire (loi 2002-2)" },
    { title: "2. Le cycle du projet", desc: "Durée de 5 ans, diagnostic, orientations, mise en œuvre, évaluation" },
    { title: "3. L'évaluation", desc: "Évaluation interne, évaluation externe, référentiel HAS et indicateurs" },
    { title: "4. La participation des acteurs", desc: "Professionnels, usagers, familles, CVS et bénéfices de la démarche participative" }
  ]},

  // SLIDE 33 — SOUS-SECTION 1
  { type: 'section', title: "1. Le projet d'établissement", subtitle: "ou de service", description: "Définition, cadre réglementaire et objectifs du document stratégique." },

  // SLIDE 34 — Définition
  { type: 'content', title: "Définition du projet d'établissement",
    body: "Le projet d'établissement ou de service est un document stratégique qui formalise les valeurs, les orientations et les objectifs d'une structure sociale ou médico-sociale. Il constitue un cadre de référence pour l'ensemble des professionnels de l'établissement.\n\nRendu obligatoire par la loi du 2 janvier 2002 rénovant l'action sociale et médico-sociale, il permet notamment de :",
    bullets: [
      "Définir les missions et les valeurs fondatrices de la structure",
      "Préciser les modalités d'accompagnement des usagers",
      "Organiser les pratiques professionnelles et la coordination",
      "Structurer les actions d'amélioration continue de la qualité"
    ] },

  // SLIDE 35 — Objectifs du projet
  { type: 'grid', title: "Les objectifs du projet d'établissement", items: [
    { title: "Donner une direction commune", desc: "Partager une vision commune du fonctionnement, des valeurs et des priorités entre tous les acteurs de la structure." },
    { title: "Améliorer la qualité", desc: "Définir les principes qui guident l'accompagnement des usagers et les standards de qualité attendus." },
    { title: "Structurer l'organisation", desc: "Préciser les modalités d'organisation, de coordination et de fonctionnement du service." },
    { title: "Répondre au cadre légal", desc: "Satisfaire aux obligations réglementaires de la loi 2002-2 et aux exigences des autorités de contrôle et de tarification." }
  ]},

  // SLIDE 36 — SOUS-SECTION 2
  { type: 'section', title: "2. Le cycle du projet", subtitle: "d'établissement", description: "Une démarche structurée sur 5 ans avec des étapes de diagnostic, mise en œuvre et évaluation." },

  // SLIDE 37 — Durée
  { type: 'content', title: "La durée du projet d'établissement",
    body: "Le projet d'établissement est généralement élaboré pour une durée de cinq ans. Cette durée permet d'inscrire les orientations dans une perspective pluriannuelle tout en organisant une démarche d'amélioration continue.\n\nÀ l'issue de cette période, le projet doit être révisé afin de tenir compte des évolutions du contexte : nouveaux besoins des publics, évolutions réglementaires, résultats des évaluations précédentes.",
    highlight: "Le renouvellement du projet est l'occasion d'un bilan approfondi et d'une remobilisation des acteurs autour d'une vision partagée." },

  // SLIDE 38 — Étapes
  { type: 'grid', title: "Les étapes du cycle du projet d'établissement", items: [
    { title: "1. Le diagnostic", desc: "Analyser la situation : besoins des publics, ressources, partenariats, forces et faiblesses de l'organisation actuelle." },
    { title: "2. Les orientations", desc: "Définir les orientations stratégiques qui guideront l'action de l'établissement pour les 5 prochaines années." },
    { title: "3. La mise en œuvre", desc: "Réaliser les actions prévues : nouvelles pratiques, nouveaux dispositifs, formations, réorganisations." },
    { title: "4. L'évaluation", desc: "Analyser les résultats des actions menées, mesurer l'atteinte des objectifs et identifier les améliorations." }
  ]},

  // SLIDE 39 — SOUS-SECTION 3
  { type: 'section', title: "3. L'évaluation", subtitle: "du projet d'établissement", description: "Démarche interne, évaluation externe et référentiel de la Haute Autorité de Santé." },

  // SLIDE 40 — Définition évaluation
  { type: 'content', title: "Définition de l'évaluation",
    body: "L'évaluation peut être définie comme un processus visant à mesurer les effets d'une action ou d'un dispositif afin d'en apprécier la pertinence et l'efficacité.\n\nDans le secteur social et médico-social, l'évaluation vise notamment à :",
    bullets: [
      "Améliorer la qualité des services rendus aux usagers",
      "Adapter les pratiques professionnelles aux besoins identifiés",
      "Renforcer la transparence et la redevabilité des organisations",
      "Satisfaire aux exigences des autorités de contrôle et de tarification"
    ] },

  // SLIDE 41 — Évaluation interne
  { type: 'content', title: "L'évaluation interne",
    body: "L'évaluation interne est réalisée par l'établissement lui-même. Elle permet aux professionnels d'analyser leurs pratiques et d'identifier les axes d'amélioration dans une logique d'auto-évaluation constructive.\n\nCette démarche repose généralement sur :",
    bullets: [
      "La constitution de groupes de travail pluridisciplinaires",
      "L'analyse de situations professionnelles concrètes",
      "La collecte d'indicateurs quantitatifs et qualitatifs",
      "La rédaction d'un rapport d'évaluation interne avec plan d'actions"
    ],
    highlight: "Le responsable d'unité joue un rôle central dans l'animation de cette démarche, en mobilisant les équipes et en facilitant l'analyse des pratiques." },

  // SLIDE 42 — Évaluation externe
  { type: 'content', title: "L'évaluation externe",
    body: "L'évaluation externe est réalisée par un organisme indépendant habilité. Elle vise à porter un regard extérieur sur le fonctionnement de l'établissement et à vérifier la cohérence entre les pratiques et les engagements du projet d'établissement.\n\nCette évaluation permet notamment :",
    bullets: [
      "De vérifier la conformité des pratiques avec les exigences réglementaires",
      "D'identifier les points forts et les axes d'amélioration",
      "De compléter les constats de l'évaluation interne par un regard indépendant",
      "De formuler des recommandations pour améliorer la qualité"
    ] },

  // SLIDE 43 — Référentiel HAS
  { type: 'content', title: "Le référentiel d'évaluation de la HAS",
    body: "Depuis la réforme du dispositif d'évaluation, la Haute Autorité de Santé (HAS) a mis en place un nouveau référentiel d'évaluation qui harmonise les démarches dans l'ensemble des établissements et services sociaux et médico-sociaux (ESSMS).\n\nCe référentiel unique remplace les anciens dispositifs d'évaluation interne et externe par une démarche intégrée et continue.",
    highlight: "Le référentiel HAS repose sur trois axes principaux qui structurent l'évaluation de la qualité des établissements." },

  // SLIDE 44 — 3 axes HAS
  { type: 'grid', title: "Les trois axes du référentiel HAS", items: [
    { title: "Axe 1 : La personne", desc: "Qualité de l'accompagnement, respect des droits, personnalisation du projet, bientraitance, autodétermination des usagers." },
    { title: "Axe 2 : Les professionnels", desc: "Qualité de vie au travail, formation continue, soutien managérial, analyse des pratiques, compétences des équipes." },
    { title: "Axe 3 : L'établissement", desc: "Gouvernance, pilotage de la qualité, gestion des risques, partenariats, démarche d'amélioration continue." }
  ]},

  // SLIDE 45 — SOUS-SECTION 4
  { type: 'section', title: "4. La participation des acteurs", subtitle: "à l'évaluation", description: "Implication des professionnels, des usagers et des familles dans la démarche qualité." },

  // SLIDE 46 — Professionnels
  { type: 'content', title: "La participation des professionnels",
    body: "Les professionnels jouent un rôle central dans l'évaluation du projet d'établissement. Leur connaissance du terrain et des pratiques est indispensable pour produire une analyse pertinente.",
    bullets: [
      "Partage de l'expérience quotidienne du terrain et des réalités de l'accompagnement",
      "Analyse collective des pratiques professionnelles lors de groupes de travail",
      "Propositions concrètes de pistes d'amélioration issues de l'expérience",
      "Participation à la rédaction du rapport d'évaluation et du plan d'actions"
    ],
    highlight: "Le responsable d'unité doit favoriser cette participation en créant un climat de confiance et en valorisant les contributions de chacun." },

  // SLIDE 47 — Usagers
  { type: 'content', title: "La participation des usagers",
    body: "Les usagers doivent être associés à l'évaluation des services qui les concernent. Leur parole est essentielle pour comprendre la qualité perçue de l'accompagnement.\n\nCette participation peut prendre différentes formes :",
    bullets: [
      "Enquêtes de satisfaction (questionnaires, entretiens)",
      "Entretiens individuels réguliers sur la qualité de l'accompagnement",
      "Participation au Conseil de la Vie Sociale (CVS)",
      "Groupes d'expression et espaces de parole collective"
    ] },

  // SLIDE 48 — Familles
  { type: 'content', title: "La participation des familles",
    body: "Dans certains secteurs, notamment dans la protection de l'enfance ou le handicap, les familles jouent un rôle important dans l'évaluation. Leur implication permet de recueillir un regard complémentaire sur la qualité de l'accompagnement.\n\nLes familles peuvent être associées via :",
    bullets: [
      "Des enquêtes de satisfaction spécifiques",
      "La participation au CVS en tant que représentants des familles",
      "Des rencontres régulières avec l'équipe d'encadrement",
      "Des groupes de parole ou des commissions thématiques"
    ] },

  // SLIDE 49 — Bénéfices
  { type: 'grid', title: "Les bénéfices de la démarche participative", items: [
    { title: "Pertinence accrue", desc: "La diversité des regards permet une analyse plus complète et plus nuancée de la qualité des services." },
    { title: "Adhésion renforcée", desc: "L'implication des acteurs dans l'évaluation favorise leur engagement dans la mise en œuvre des améliorations." },
    { title: "Amélioration des pratiques", desc: "Les retours croisés des professionnels, usagers et familles enrichissent les propositions d'amélioration." },
    { title: "Légitimité de la démarche", desc: "La participation de tous les acteurs renforce la crédibilité et la légitimité du processus d'évaluation." }
  ]},

  // SLIDE 50 — Conclusion Chap 2
  { type: 'content', title: "Conclusion — Chapitre 2",
    body: "L'évaluation du projet d'établissement constitue une démarche essentielle pour améliorer la qualité des services proposés aux usagers.\n\nLe responsable d'unité joue un rôle important en :",
    bullets: [
      "Mobilisant les équipes autour de la démarche d'évaluation",
      "Facilitant l'analyse collective des pratiques professionnelles",
      "Contribuant à la réflexion stratégique de l'établissement",
      "Favorisant la participation des usagers et des familles"
    ],
    highlight: "Cette démarche d'évaluation permet d'inscrire l'action des établissements sociaux et médico-sociaux dans une logique d'amélioration continue au service des personnes accompagnées." },

  // ============================================================
  // CHAPITRE 3 — CONCEVOIR LE PROJET DE L'UIS
  // ============================================================
  // SLIDE 51 — SECTION
  { type: 'section', title: "Chapitre 3", subtitle: "Concevoir le projet de l'UIS en cohérence avec le projet d'établissement", description: "Méthodologie de projet, articulation stratégique, élaboration participative et formalisation." },

  // SLIDE 52 — INTRO CHAP 3
  { type: 'content', title: "Introduction — Chapitre 3",
    body: "Pour être opérationnel, le projet d'établissement doit être décliné à l'échelle des unités d'intervention sociale. Chaque unité adapte les orientations générales à son contexte spécifique, aux besoins de ses publics et aux ressources disponibles.\n\nLe projet de service constitue un outil de pilotage essentiel. Il permet de traduire les orientations stratégiques en actions concrètes et adaptées aux réalités du terrain.\n\nLe responsable d'unité joue un rôle central dans cette élaboration : il mobilise les équipes, analyse les besoins et coordonne la construction d'un projet cohérent." },

  // SLIDE 53 — OBJECTIFS CHAP 3
  { type: 'content', title: "Objectifs pédagogiques — Chapitre 3",
    body: "À l'issue de ce module, le professionnel doit être capable de :",
    bullets: [
      "Concevoir un projet d'unité en cohérence avec le projet d'établissement",
      "Utiliser une méthodologie de conduite de projet structurée",
      "Appliquer la méthode SMART pour définir des objectifs opérationnels",
      "Mobiliser les équipes dans l'élaboration participative du projet",
      "Formaliser et structurer le document projet"
    ] },

  // SLIDE 54 — SOMMAIRE CHAP 3
  { type: 'grid', title: "Sommaire — Chapitre 3 : Concevoir le projet de l'UIS", items: [
    { title: "1. La méthodologie de projet", desc: "Étapes : diagnostic, objectifs SMART, mise en œuvre, évaluation" },
    { title: "2. L'articulation stratégique", desc: "Cohérence entre projet d'établissement et projet de service, déclinaison opérationnelle" },
    { title: "3. L'élaboration participative", desc: "Implication des équipes, groupes de travail, participation des usagers" },
    { title: "4. La formalisation", desc: "Rédaction du document, validation institutionnelle, suivi et ajustement" }
  ]},

  // SLIDE 55 — SOUS-SECTION 1
  { type: 'section', title: "1. La méthodologie de projet", subtitle: "dans le secteur social", description: "Les étapes structurantes pour concevoir et conduire un projet de service." },

  // SLIDE 56 — Définition
  { type: 'content', title: "La méthodologie de projet : définition",
    body: "La méthodologie de projet correspond à un ensemble d'étapes et d'outils permettant de concevoir, mettre en œuvre et évaluer une action ou un programme.\n\nDans le secteur social et médico-social, la démarche de projet vise à :",
    bullets: [
      "Améliorer la qualité de l'accompagnement des usagers",
      "Structurer les pratiques professionnelles autour d'objectifs partagés",
      "Développer des actions adaptées aux besoins identifiés",
      "Optimiser l'utilisation des ressources disponibles"
    ] },

  // SLIDE 57 — Diagnostic
  { type: 'content', title: "Étape 1 : Le diagnostic",
    body: "La première étape consiste à analyser la situation de départ. Le diagnostic est le socle sur lequel repose l'ensemble de la démarche de projet.\n\nLe diagnostic permet d'identifier :",
    bullets: [
      "Les besoins des usagers (nature, intensité, évolution)",
      "Les ressources humaines et matérielles disponibles",
      "Les difficultés rencontrées dans l'organisation du service",
      "Les attentes des partenaires et du territoire"
    ],
    highlight: "Le diagnostic peut s'appuyer sur : les observations des professionnels, les données statistiques, les retours des usagers et des familles, et les échanges avec les partenaires." },

  // SLIDE 58 — Objectifs SMART
  { type: 'content', title: "Étape 2 : La définition des objectifs (méthode SMART)",
    body: "À partir du diagnostic, il est possible de définir les objectifs du projet. Les objectifs doivent être formulés de manière précise pour faciliter leur mise en œuvre et leur évaluation.\n\nLa méthode SMART propose 5 critères :",
    bullets: [
      "Spécifique : l'objectif décrit précisément ce qui doit être réalisé",
      "Mesurable : des indicateurs permettent de vérifier l'atteinte de l'objectif",
      "Atteignable : l'objectif est réaliste compte tenu des ressources disponibles",
      "Réaliste : l'objectif est pertinent et en lien avec les besoins identifiés",
      "Temporellement défini : un délai de réalisation est fixé"
    ],
    highlight: "Exemple : « Réduire de 20% les délais d'attente pour un premier entretien dans les 6 prochains mois. »" },

  // SLIDE 59 — Mise en œuvre
  { type: 'content', title: "Étape 3 : La mise en œuvre du projet",
    body: "Cette étape correspond à la réalisation concrète des actions prévues dans le projet. Le responsable d'unité joue un rôle d'orchestrateur dans cette phase opérationnelle.",
    bullets: [
      "Mobilisation des professionnels autour des actions à conduire",
      "Organisation et allocation des ressources nécessaires",
      "Coordination des activités et gestion du calendrier",
      "Communication régulière sur l'avancement du projet",
      "Gestion des ajustements nécessaires en cours de réalisation"
    ] },

  // SLIDE 60 — Évaluation projet
  { type: 'content', title: "Étape 4 : L'évaluation du projet",
    body: "L'évaluation permet d'analyser les résultats obtenus et de mesurer l'efficacité des actions mises en place. C'est une étape indispensable pour inscrire le projet dans une démarche d'amélioration continue.",
    bullets: [
      "Vérifier si les objectifs fixés ont été atteints (indicateurs SMART)",
      "Identifier les points forts et les réussites à capitaliser",
      "Repérer les difficultés rencontrées et les facteurs d'échec",
      "Formuler des propositions d'ajustement pour la suite"
    ] },

  // SLIDE 61 — SOUS-SECTION 2
  { type: 'section', title: "2. L'articulation stratégique", subtitle: "entre projet d'établissement et projet d'unité", description: "Assurer la cohérence entre les orientations institutionnelles et les actions opérationnelles." },

  // SLIDE 62 — Cohérence
  { type: 'content', title: "La cohérence stratégique",
    body: "Le projet d'unité doit être cohérent avec les orientations du projet d'établissement. Les objectifs définis dans l'unité doivent contribuer aux priorités stratégiques de la structure.\n\nCette cohérence garantit que l'ensemble des actions menées au sein de l'établissement convergent vers les mêmes finalités.",
    highlight: "Exemple : si le projet d'établissement met l'accent sur l'inclusion des personnes en situation de handicap, l'unité peut développer des actions favorisant l'autonomie, la participation sociale et l'accès aux droits des usagers." },

  // SLIDE 63 — Déclinaison
  { type: 'content', title: "La déclinaison des orientations stratégiques",
    body: "La déclinaison consiste à transformer les orientations générales de l'établissement en objectifs opérationnels adaptés au contexte spécifique de l'unité.\n\nCes objectifs opérationnels peuvent concerner :",
    bullets: [
      "L'amélioration de pratiques professionnelles spécifiques à l'unité",
      "Le développement de nouvelles activités ou de nouveaux dispositifs",
      "L'amélioration de la coordination entre professionnels de l'unité",
      "Le renforcement des partenariats locaux pertinents pour l'unité"
    ] },

  // SLIDE 64 — Objectifs opérationnels
  { type: 'content', title: "Les objectifs opérationnels",
    body: "Les objectifs opérationnels correspondent aux actions concrètes mises en place dans l'unité. Ils constituent la traduction pratique des orientations stratégiques.\n\nIls doivent être :",
    bullets: [
      "Précis : décrivant clairement les actions à mettre en œuvre",
      "Réalisables : tenant compte des ressources et contraintes de l'unité",
      "Adaptés : cohérents avec le contexte et les besoins des publics de l'unité",
      "Évaluables : associés à des indicateurs de suivi et de résultat"
    ],
    highlight: "Les objectifs opérationnels constituent la base du projet de service et permettent de mesurer concrètement les progrès réalisés." },

  // SLIDE 65 — SOUS-SECTION 3
  { type: 'section', title: "3. L'élaboration participative", subtitle: "du projet de service", description: "Mobilisation des professionnels, animation de groupes de travail et implication des usagers." },

  // SLIDE 66 — Importance participation
  { type: 'content', title: "L'importance de la participation des équipes",
    body: "La réussite d'un projet de service repose en grande partie sur l'implication des professionnels. Un projet construit « du haut » sans concertation sera difficilement approprié par les équipes.\n\nLa participation des équipes permet :",
    bullets: [
      "De valoriser les compétences et l'expertise des professionnels",
      "De renforcer l'adhésion et l'engagement autour du projet commun",
      "D'améliorer la pertinence des actions proposées grâce à la connaissance du terrain",
      "De favoriser une culture de coopération et de responsabilité partagée"
    ],
    highlight: "Le responsable d'unité doit créer les conditions de cette participation en organisant des espaces de réflexion collective et en valorisant les contributions." },

  // SLIDE 67 — Groupes de travail
  { type: 'content', title: "L'animation de groupes de travail",
    body: "Les groupes de travail constituent un outil privilégié pour élaborer collectivement le projet de service. Le responsable d'unité joue un rôle d'animateur dans ces espaces.",
    bullets: [
      "Analyser les pratiques professionnelles et identifier les besoins d'amélioration",
      "Proposer des actions concrètes à intégrer dans le projet",
      "Favoriser les échanges constructifs entre les différents métiers",
      "Structurer les propositions pour les présenter à la direction"
    ],
    highlight: "Le rôle de l'animateur : organiser les réunions, faciliter les échanges, recentrer les débats, synthétiser les propositions et garantir un climat de confiance." },

  // SLIDE 68 — Participation usagers/familles
  { type: 'content', title: "La participation des usagers et des familles",
    body: "Dans une logique de démocratie participative et de respect des droits des personnes, il est essentiel d'associer les usagers et leurs familles à l'élaboration du projet.\n\nCette participation peut prendre différentes formes :",
    bullets: [
      "Consultation directe des usagers sur leurs besoins et attentes",
      "Enquêtes de satisfaction pour recueillir leur avis sur l'accompagnement",
      "Participation au Conseil de la Vie Sociale (CVS) pour contribuer aux orientations",
      "Groupes d'expression permettant aux usagers de s'exprimer collectivement"
    ] },

  // SLIDE 69 — SOUS-SECTION 4
  { type: 'section', title: "4. La formalisation", subtitle: "du projet de service", description: "Rédaction, validation institutionnelle et suivi de la mise en œuvre." },

  // SLIDE 70 — Rédaction
  { type: 'content', title: "La rédaction du document projet",
    body: "Le projet de service doit être formalisé dans un document structuré, accessible et opérationnel. Ce document constitue la feuille de route de l'unité pour les années à venir.\n\nLe document projet comporte généralement :",
    bullets: [
      "1. Une présentation de l'unité (contexte, missions, publics, équipe)",
      "2. L'analyse du contexte et des besoins (diagnostic partagé)",
      "3. Les objectifs du projet (stratégiques et opérationnels, formulés en SMART)",
      "4. Les actions prévues (fiches actions avec responsables, calendrier, moyens)",
      "5. Les modalités d'évaluation (indicateurs, bilans, fréquence)"
    ],
    highlight: "La rédaction doit être claire et accessible pour l'ensemble des professionnels de l'unité." },

  // SLIDE 71 — Validation
  { type: 'content', title: "La validation institutionnelle du projet",
    body: "Une fois rédigé, le projet de service doit être validé par la direction de l'établissement. Cette étape est essentielle pour garantir la cohérence institutionnelle.\n\nLa validation permet :",
    bullets: [
      "D'assurer la cohérence avec le projet d'établissement et ses orientations",
      "De garantir l'adhésion et le soutien institutionnel au projet",
      "De sécuriser les ressources nécessaires à la mise en œuvre",
      "De légitimer les actions prévues auprès de l'ensemble des acteurs"
    ] },

  // SLIDE 72 — Suivi
  { type: 'content', title: "Le suivi du projet de service",
    body: "La mise en œuvre du projet doit faire l'objet d'un suivi régulier afin de vérifier l'avancement des actions et d'ajuster le cas échéant.",
    bullets: [
      "Réunions de pilotage régulières (trimestrielles ou semestrielles)",
      "Indicateurs de suivi permettant de mesurer l'avancement",
      "Bilans intermédiaires pour identifier les réussites et les obstacles",
      "Ajustement des actions en fonction des résultats observés"
    ],
    highlight: "Le suivi du projet est un processus dynamique qui permet de maintenir la cohérence entre les objectifs et les réalisations." },

  // SLIDE 73 — Conclusion Chap 3
  { type: 'content', title: "Conclusion — Chapitre 3",
    body: "La conception du projet d'unité constitue une compétence essentielle du responsable d'unité dans le cadre du CAFERUIS.\n\nElle implique :",
    bullets: [
      "Une analyse approfondie des besoins des publics et du contexte",
      "Une articulation rigoureuse avec les orientations stratégiques de l'établissement",
      "Une mobilisation active des professionnels dans une démarche participative",
      "Une formalisation structurée et un suivi régulier des actions"
    ],
    highlight: "Le projet de service est un outil structurant qui donne du sens à l'action collective et améliore la qualité de l'accompagnement." },

  // ============================================================
  // CHAPITRE 4 — DÉVELOPPER UNE VEILLE PROFESSIONNELLE
  // ============================================================
  // SLIDE 74 — SECTION
  { type: 'section', title: "Chapitre 4", subtitle: "Développer une veille professionnelle", description: "Veille juridique, innovation sociale, outils de veille et diffusion au sein de l'équipe." },

  // SLIDE 75 — INTRO CHAP 4
  { type: 'content', title: "Introduction — Chapitre 4",
    body: "Dans le secteur social et médico-social, les cadres évoluent dans un environnement en constante transformation. Les politiques publiques, les cadres réglementaires, les besoins des publics et les pratiques professionnelles changent régulièrement.\n\nLa veille professionnelle constitue une compétence essentielle : elle permet d'anticiper les transformations, d'identifier les innovations et d'adapter les pratiques.\n\nLa veille ne se limite pas à la recherche d'informations. Elle consiste à collecter, analyser et diffuser des informations pertinentes afin d'éclairer la prise de décision." },

  // SLIDE 76 — OBJECTIFS CHAP 4
  { type: 'content', title: "Objectifs pédagogiques — Chapitre 4",
    body: "À l'issue de ce module, le professionnel doit être capable de :",
    bullets: [
      "Organiser une démarche de veille professionnelle structurée et régulière",
      "Identifier les sources d'information pertinentes dans le secteur",
      "Analyser les évolutions réglementaires et institutionnelles",
      "Repérer les innovations sociales et les bonnes pratiques transférables",
      "Diffuser les informations utiles au sein de l'équipe"
    ] },

  // SLIDE 77 — SOMMAIRE CHAP 4
  { type: 'grid', title: "Sommaire — Chapitre 4 : Veille professionnelle", items: [
    { title: "1. La veille professionnelle", desc: "Définition, enjeux et objectifs de la veille dans le secteur social" },
    { title: "2. La veille juridique", desc: "Suivi des évolutions législatives, sources officielles, abonnements" },
    { title: "3. L'innovation sociale", desc: "Benchmarking, expériences innovantes, initiatives territoriales" },
    { title: "4. Les outils de veille", desc: "Curation, newsletters, réseaux professionnels, organisation de la diffusion" }
  ]},

  // SLIDE 78 — SOUS-SECTION 1
  { type: 'section', title: "1. La veille professionnelle", subtitle: "Définition et enjeux", description: "Comprendre le processus de veille et ses objectifs dans le secteur social." },

  // SLIDE 79 — Définition
  { type: 'content', title: "Définition de la veille professionnelle",
    body: "La veille professionnelle peut être définie comme un processus continu de collecte, d'analyse et de diffusion d'informations permettant d'anticiper les évolutions d'un secteur d'activité.\n\nDans le secteur social et médico-social, la veille permet notamment de :",
    bullets: [
      "Suivre l'évolution des politiques sociales (lois, plans, schémas directeurs)",
      "Identifier les nouvelles pratiques d'accompagnement validées",
      "Anticiper les changements réglementaires impactant l'établissement",
      "Repérer les innovations organisationnelles transférables"
    ],
    highlight: "La veille constitue un outil d'aide à la décision pour les responsables d'unité et un levier d'amélioration continue." },

  // SLIDE 80 — Objectifs veille
  { type: 'grid', title: "Les objectifs de la veille professionnelle", items: [
    { title: "Anticiper les évolutions", desc: "Être capable de détecter les signaux faibles et d'anticiper les transformations du secteur avant qu'elles ne s'imposent." },
    { title: "Améliorer les pratiques", desc: "Identifier des méthodes innovantes, des expériences inspirantes et des approches validées par la recherche." },
    { title: "Soutenir la stratégie", desc: "Alimenter les projets d'établissement et de service avec des données actualisées et des analyses pertinentes." },
    { title: "Renforcer l'expertise", desc: "Maintenir et développer les compétences professionnelles des cadres et des équipes." }
  ]},

  // SLIDE 81 — SOUS-SECTION 2
  { type: 'section', title: "2. La veille juridique", subtitle: "et réglementaire", description: "Suivi des évolutions législatives et réglementaires du secteur social." },

  // SLIDE 82 — Enjeux veille réglementaire
  { type: 'content', title: "Les enjeux de la veille réglementaire",
    body: "Le secteur social et médico-social est fortement encadré par des textes législatifs et réglementaires. La méconnaissance d'une évolution juridique peut exposer l'établissement à des risques de non-conformité.\n\nLes établissements doivent respecter :",
    bullets: [
      "Les lois sociales (loi 2002-2, loi ASV, loi 3DS, etc.)",
      "Les décrets et arrêtés d'application",
      "Les recommandations de bonnes pratiques de la HAS",
      "Les conventions collectives et accords de branche"
    ],
    highlight: "La veille réglementaire permet d'identifier rapidement les évolutions juridiques susceptibles d'impacter le fonctionnement de l'établissement." },

  // SLIDE 83 — Publications officielles
  { type: 'grid', title: "Les sources d'information juridiques", items: [
    { title: "Publications officielles", desc: "Journal officiel, sites des ministères (Solidarités, Santé), Légifrance, circulaires et instructions" },
    { title: "Autorités de régulation", desc: "HAS, ARS, CNSA, Défenseur des droits, CNIL (données personnelles)" },
    { title: "Organisations professionnelles", desc: "FEHAP, NEXEM, UNIOPSS, fédérations patronales et syndicales du secteur" },
    { title: "Presse spécialisée", desc: "ASH (Actualités Sociales Hebdomadaires), TSA (Travail Social Actualités), Lien Social" }
  ]},

  // SLIDE 84 — Recommandations professionnelles
  { type: 'content', title: "Les recommandations professionnelles",
    body: "Dans le secteur médico-social, la HAS (ex-ANESM) publie des recommandations de bonnes pratiques professionnelles (RBPP) destinées à améliorer la qualité de l'accompagnement.\n\nCes recommandations constituent une source incontournable pour les professionnels. Elles couvrent de nombreux domaines :",
    bullets: [
      "Bientraitance et promotion des droits des personnes",
      "Personnalisation de l'accompagnement et projet personnalisé",
      "Participation des usagers et expression des personnes",
      "Qualité de vie en établissement et conduite de l'évaluation"
    ] },

  // SLIDE 85 — Abonnements
  { type: 'content', title: "Les abonnements juridiques et bulletins spécialisés",
    body: "De nombreuses organisations proposent des services de veille juridique permettant de recevoir des informations ciblées sur les évolutions réglementaires.\n\nCes services prennent différentes formes :",
    bullets: [
      "Newsletters juridiques hebdomadaires ou mensuelles",
      "Bulletins spécialisés par secteur (handicap, protection de l'enfance, personnes âgées)",
      "Bases documentaires en ligne avec moteur de recherche",
      "Alertes automatiques sur les thématiques suivies"
    ],
    highlight: "L'investissement dans des outils de veille juridique est essentiel pour garantir la conformité réglementaire de l'établissement." },

  // SLIDE 86 — SOUS-SECTION 3
  { type: 'section', title: "3. L'innovation sociale", subtitle: "dans le secteur social et médico-social", description: "Benchmarking, expériences innovantes et initiatives territoriales." },

  // SLIDE 87 — Définition innovation
  { type: 'content', title: "L'innovation sociale : définition",
    body: "L'innovation sociale correspond à l'ensemble des initiatives visant à apporter des réponses nouvelles aux besoins sociaux. Elle se distingue de l'innovation technologique par sa dimension humaine et relationnelle.\n\nDans le secteur social, l'innovation peut concerner :",
    bullets: [
      "Les modes d'accompagnement des usagers (pair-aidance, empowerment, rétablissement)",
      "L'organisation des services (équipes mobiles, dispositifs intégrés)",
      "Les partenariats entre institutions (plateformes territoriales, GCS)",
      "Les outils numériques au service de l'accompagnement (télé-suivi, applications)"
    ],
    highlight: "L'innovation sociale vise à améliorer la qualité de l'accompagnement et à répondre à des problématiques émergentes de manière créative et adaptée." },

  // SLIDE 88 — Benchmarking
  { type: 'content', title: "Le benchmarking : s'inspirer pour innover",
    body: "Le benchmarking consiste à analyser les pratiques d'autres organisations afin de s'en inspirer pour améliorer son propre fonctionnement. C'est une démarche proactive d'apprentissage organisationnel.\n\nCette démarche permet de :",
    bullets: [
      "Identifier des solutions innovantes expérimentées ailleurs",
      "Comparer les pratiques professionnelles entre établissements similaires",
      "Développer de nouvelles approches d'accompagnement",
      "Éviter de « réinventer la roue » en capitalisant sur les expériences existantes"
    ],
    highlight: "Le benchmarking peut porter sur l'organisation des services, les méthodes d'intervention, les dispositifs d'accompagnement ou les modalités de partenariat." },

  // SLIDE 89 — Expériences innovantes
  { type: 'content', title: "Le repérage des expériences innovantes",
    body: "Les expériences innovantes peuvent être repérées à travers différents canaux :",
    bullets: [
      "Les projets expérimentaux financés par des appels à projets (ARS, CNSA, État)",
      "Les initiatives locales portées par des associations ou des collectivités",
      "Les programmes pilotes nationaux ou européens",
      "Les publications de recherche-action et les retours d'expérience",
      "Les prix et distinctions décernés dans le secteur social"
    ],
    highlight: "Le responsable d'unité peut s'appuyer sur ces expériences pour proposer des évolutions dans son service et enrichir le projet d'établissement." },

  // SLIDE 90 — SOUS-SECTION 4
  { type: 'section', title: "4. Les outils de veille", subtitle: "professionnelle", description: "Curation de contenu, newsletters, réseaux et organisation de la diffusion." },

  // SLIDE 91 — Curation
  { type: 'content', title: "La curation de contenu",
    body: "La curation de contenu consiste à sélectionner, organiser et partager des informations pertinentes afin de les rendre accessibles à d'autres professionnels. C'est une compétence clé à l'ère de la surabondance informationnelle.",
    bullets: [
      "Synthétiser les informations importantes en fiches ou notes de synthèse",
      "Faciliter leur diffusion auprès des équipes et de la direction",
      "Alimenter la réflexion collective lors des réunions d'équipe",
      "Constituer une base documentaire partagée et actualisée"
    ] },

  // SLIDE 92 — Newsletters
  { type: 'content', title: "Les newsletters professionnelles",
    body: "Les newsletters constituent un outil simple et efficace pour suivre l'actualité du secteur sans y consacrer un temps excessif.\n\nElles permettent de recevoir régulièrement des informations sur :",
    bullets: [
      "Les évolutions réglementaires impactant le secteur",
      "Les nouvelles pratiques professionnelles et les études de référence",
      "Les innovations sociales et les expériences à suivre",
      "Les événements professionnels (colloques, formations, journées d'étude)"
    ] },

  // SLIDE 93 — Réseaux professionnels
  { type: 'content', title: "Les réseaux professionnels",
    body: "Les réseaux professionnels jouent un rôle fondamental dans la diffusion des connaissances et le partage d'expériences entre pairs.\n\nIls permettent aux professionnels de :",
    bullets: [
      "Partager leurs expériences et leurs questionnements professionnels",
      "Échanger sur leurs pratiques et bénéficier de regards croisés",
      "Découvrir de nouvelles initiatives inspirantes",
      "Développer des partenariats et des collaborations inter-établissements"
    ],
    highlight: "Ces réseaux peuvent être institutionnels (fédérations), territoriaux (réseaux locaux) ou numériques (groupes professionnels en ligne, forums spécialisés)." },

  // SLIDE 94 — Organisation veille
  { type: 'content', title: "Organiser la diffusion de la veille",
    body: "Pour être efficace, la veille professionnelle doit être organisée et partagée. L'information n'a de valeur que si elle est diffusée au bon moment aux bonnes personnes.\n\nLe responsable d'unité peut :",
    bullets: [
      "Définir les thématiques prioritaires de veille pour son service",
      "Sélectionner les sources d'information les plus pertinentes",
      "Désigner des référents thématiques au sein de l'équipe",
      "Organiser des temps de partage réguliers (point d'actu en réunion d'équipe)",
      "Constituer un espace documentaire accessible (dossier partagé, intranet)"
    ] },

  // SLIDE 95 — Conclusion Chap 4
  { type: 'content', title: "Conclusion — Chapitre 4",
    body: "La veille professionnelle constitue un outil essentiel pour accompagner les évolutions du secteur social et médico-social.\n\nElle permet aux responsables d'unité :",
    bullets: [
      "D'anticiper les transformations réglementaires et leurs impacts",
      "D'identifier les innovations sociales et les bonnes pratiques transférables",
      "D'adapter les pratiques professionnelles aux évolutions du secteur",
      "De renforcer la capacité d'adaptation et d'innovation de l'établissement"
    ],
    highlight: "En développant une démarche de veille structurée, le responsable d'unité contribue à positionner son établissement comme un acteur proactif et innovant." },

  // ============================================================
  // CHAPITRE 5 — COMMUNIQUER SUR LE PROJET
  // ============================================================
  // SLIDE 96 — SECTION
  { type: 'section', title: "Chapitre 5", subtitle: "Communiquer sur le projet d'établissement ou de service", description: "Communication interne, mobilisation des équipes, communication externe et image institutionnelle." },

  // SLIDE 97 — INTRO CHAP 5
  { type: 'content', title: "Introduction — Chapitre 5",
    body: "Le projet d'établissement, pour être efficace, doit être connu, compris et partagé par l'ensemble des acteurs. La communication joue un rôle essentiel dans sa mise en œuvre.\n\nLe responsable d'unité occupe une position clé : en tant que cadre intermédiaire, il assure la diffusion des orientations stratégiques auprès des équipes et contribue à promouvoir les actions de l'unité auprès des partenaires.\n\nLa communication s'inscrit dans une double dimension :",
    bullets: [
      "Communication interne : mobiliser les professionnels et faire vivre le projet au quotidien",
      "Communication externe : valoriser les actions et renforcer les partenariats territoriaux"
    ] },

  // SLIDE 98 — OBJECTIFS CHAP 5
  { type: 'content', title: "Objectifs pédagogiques — Chapitre 5",
    body: "À l'issue de ce module, le professionnel doit être capable de :",
    bullets: [
      "Diffuser les orientations du projet d'établissement auprès des équipes",
      "Mobiliser les professionnels autour des objectifs et des valeurs du projet",
      "Valoriser les actions de l'unité auprès des partenaires et du territoire",
      "Développer une communication institutionnelle adaptée et cohérente"
    ] },

  // SLIDE 99 — SOMMAIRE CHAP 5
  { type: 'grid', title: "Sommaire — Chapitre 5 : Communiquer sur le projet", items: [
    { title: "1. La communication interne", desc: "Définition, faire vivre le projet, outils de communication, rôle du cadre" },
    { title: "2. La communication externe", desc: "Promouvoir les actions, partenaires, médias locaux, image institutionnelle" }
  ]},

  // SLIDE 100 — SOUS-SECTION 1
  { type: 'section', title: "1. La communication interne", subtitle: "autour du projet d'établissement", description: "Faire vivre le projet au quotidien et mobiliser les équipes." },

  // SLIDE 101 — Définition
  { type: 'content', title: "La communication interne : définition",
    body: "La communication interne correspond à l'ensemble des échanges d'informations au sein de l'organisation. Elle constitue le socle de la cohésion d'équipe et du fonctionnement collectif.\n\nElle vise à :",
    bullets: [
      "Informer les professionnels des orientations, décisions et évolutions",
      "Favoriser la circulation des informations entre les différents niveaux",
      "Renforcer la cohésion des équipes autour de valeurs et d'objectifs partagés",
      "Garantir que les orientations stratégiques sont comprises et mises en œuvre"
    ] },

  // SLIDE 102 — Faire vivre le projet
  { type: 'content', title: "Faire vivre le projet au quotidien",
    body: "Le projet d'établissement ne doit pas être un simple document rangé dans un tiroir. Il doit constituer un outil vivant qui guide les pratiques professionnelles au quotidien.\n\nLe responsable d'unité contribue à cette dynamique en :",
    bullets: [
      "Rappelant régulièrement les valeurs et les objectifs du projet en réunion",
      "Intégrant les orientations du projet dans les décisions quotidiennes",
      "Valorisant et célébrant les initiatives qui s'inscrivent dans les objectifs",
      "Faisant le lien entre les actions individuelles et la vision collective",
      "Organisant des temps de bilan et de réflexion sur l'avancement du projet"
    ],
    highlight: "Un projet approprié par les équipes produit des effets concrets sur la qualité de l'accompagnement des usagers." },

  // SLIDE 103 — Réunions
  { type: 'grid', title: "Les outils de communication interne", items: [
    { title: "Réunions d'équipe", desc: "Espace privilégié pour présenter les orientations, échanger sur les pratiques et analyser les actions en cours." },
    { title: "Supports écrits", desc: "Notes internes, documents de synthèse, affichage des orientations, fiches actions et bilans." },
    { title: "Outils numériques", desc: "Intranet, messagerie professionnelle, plateformes collaboratives, espaces de partage de documents." },
    { title: "Temps informels", desc: "Échanges au quotidien, points rapides, moments de convivialité qui renforcent la cohésion d'équipe." }
  ]},

  // SLIDE 104 — Rôle du cadre
  { type: 'content', title: "Le rôle du cadre dans la mobilisation des équipes",
    body: "Le responsable d'unité doit veiller à mobiliser durablement les professionnels autour du projet d'établissement. Cette mobilisation ne se décrète pas : elle se construit progressivement par une communication régulière et valorisante.",
    bullets: [
      "Clarifier les objectifs et expliquer le sens des actions demandées",
      "Valoriser les contributions individuelles et collectives des professionnels",
      "Reconnaître le travail accompli et célébrer les réussites",
      "Maintenir une communication transparente et régulière sur l'avancement",
      "Associer les professionnels aux décisions qui les concernent"
    ],
    highlight: "Une communication régulière, transparente et valorisante est la clé pour maintenir la dynamique collective autour du projet." },

  // SLIDE 105 — Transition vers externe
  { type: 'section', title: "2. La communication externe", subtitle: "des établissements sociaux", description: "Valoriser les actions, développer les partenariats et renforcer l'image institutionnelle." },

  // SLIDE 106 — Définition externe
  { type: 'content', title: "La communication externe : définition et enjeux",
    body: "La communication externe correspond aux actions de communication destinées aux acteurs extérieurs à l'organisation. Elle contribue à inscrire l'établissement dans son territoire et à renforcer sa visibilité.\n\nElle vise notamment à :",
    bullets: [
      "Faire connaître les missions et les actions de l'établissement",
      "Développer et entretenir les partenariats stratégiques",
      "Valoriser les initiatives innovantes du service",
      "Renforcer la crédibilité et la légitimité de la structure"
    ] },

  // SLIDE 107 — Promouvoir actions
  { type: 'content', title: "Promouvoir les actions de l'unité",
    body: "Le responsable d'unité peut participer activement à la promotion des actions menées dans son service, en coordination avec la direction.\n\nCette promotion peut concerner :",
    bullets: [
      "Les projets innovants développés par l'équipe",
      "Les actions de prévention menées auprès des publics",
      "Les initiatives de participation des usagers",
      "Les résultats des évaluations et les améliorations réalisées"
    ],
    highlight: "La valorisation des actions contribue à renforcer la visibilité de l'établissement et à motiver les professionnels." },

  // SLIDE 108 — Partenaires
  { type: 'content', title: "Les relations avec les partenaires",
    body: "La communication externe joue un rôle important dans le développement et le maintien des partenariats. Le travail en réseau est essentiel dans le secteur social.\n\nLes partenaires peuvent inclure :",
    bullets: [
      "Les institutions publiques (ARS, Conseil départemental, MDPH, PJJ)",
      "Les associations et structures du secteur social et médico-social",
      "Les acteurs du secteur sanitaire (hôpitaux, médecins, CMP)",
      "Les acteurs de l'éducation, de l'emploi et du logement"
    ],
    highlight: "Une communication régulière et de qualité permet de maintenir des relations de confiance et de faciliter la coopération territoriale." },

  // SLIDE 109 — Médias
  { type: 'content', title: "Les relations avec les médias et le territoire",
    body: "Dans certains cas, les établissements peuvent communiquer avec les médias locaux et les acteurs du territoire afin de valoriser certaines initiatives et de lutter contre les représentations négatives.\n\nCette communication peut concerner :",
    bullets: [
      "Des événements organisés par l'établissement (journées portes ouvertes, colloques)",
      "Des projets innovants méritant d'être connus (expérimentations, partenariats)",
      "Des actions de sensibilisation auprès du grand public",
      "La participation à des réseaux territoriaux et des instances de coordination"
    ],
    highlight: "Cette communication doit être organisée en coordination avec la direction de l'établissement et respecter les règles de confidentialité." },

  // SLIDE 110 — Image institutionnelle
  { type: 'content', title: "Les enjeux de l'image institutionnelle",
    body: "La communication externe contribue à construire et à préserver l'image de l'établissement. Une image positive facilite le recrutement, les partenariats et la confiance des usagers et des familles.\n\nUne communication cohérente permet :",
    bullets: [
      "De renforcer la crédibilité de la structure auprès des autorités",
      "De valoriser les compétences et l'engagement des professionnels",
      "De promouvoir les valeurs humanistes de l'établissement",
      "De faciliter l'attractivité pour le recrutement de nouveaux professionnels"
    ],
    highlight: "Le responsable d'unité doit veiller à ce que les messages diffusés soient cohérents avec les orientations du projet d'établissement." },

  // SLIDE 111 — Conclusion Chap 5
  { type: 'content', title: "Conclusion — Chapitre 5",
    body: "La communication autour du projet d'établissement constitue un levier essentiel pour assurer sa mise en œuvre effective et renforcer l'implication de tous les acteurs.\n\nLe responsable d'unité joue un rôle central en :",
    bullets: [
      "Diffusant les orientations stratégiques auprès des équipes avec pédagogie",
      "Mobilisant les professionnels autour du projet et de ses valeurs",
      "Valorisant les actions du service auprès des partenaires et du territoire",
      "Contribuant à construire une image institutionnelle positive et cohérente"
    ],
    highlight: "Cette communication contribue à renforcer la cohérence des pratiques et à améliorer la visibilité de l'établissement sur son territoire." },

  // ============================================================
  // CONCLUSION GÉNÉRALE DU BLOC 4
  // ============================================================
  // SLIDE 112 — Conclusion générale
  { type: 'content', title: "Conclusion générale du Bloc 4",
    body: "Le Bloc de compétences 4 du CAFERUIS forme le responsable d'unité à contribuer activement au projet d'établissement ou de service.\n\nCette contribution repose sur cinq piliers complémentaires :",
    bullets: [
      "L'expertise technique : analyser les besoins, anticiper les évolutions, conseiller la direction",
      "L'évaluation : participer à l'évaluation interne et externe, mobiliser le référentiel HAS",
      "La conception de projet : élaborer un projet de service cohérent, participatif et évaluable",
      "La veille professionnelle : suivre les évolutions réglementaires et repérer les innovations",
      "La communication : faire vivre le projet en interne et valoriser les actions en externe"
    ] },

  // SLIDE 113 — Compétences clés
  { type: 'grid', title: "Compétences clés du Bloc 4 — Synthèse", items: [
    { title: "Expertise et diagnostic", desc: "Analyser les besoins, mobiliser les données, formuler des préconisations stratégiques fondées sur le terrain." },
    { title: "Évaluation et qualité", desc: "Conduire l'évaluation interne, contribuer à l'externe, utiliser le référentiel HAS pour l'amélioration continue." },
    { title: "Conduite de projet", desc: "Méthodologie SMART, élaboration participative, articulation avec le projet d'établissement, suivi et ajustement." },
    { title: "Veille et communication", desc: "Veille juridique et innovation sociale, diffusion des informations, communication interne et externe." }
  ]},

  // SLIDE 114 — FÉLICITATIONS
  { type: 'congratulations', title: "Bloc 4 terminé !", message: "Vous avez terminé le Bloc de compétences 4 du CAFERUIS.\n\nVous maîtrisez désormais les enjeux de la contribution au projet d'établissement : expertise technique, évaluation, conception de projet, veille professionnelle et communication institutionnelle.\n\nCes compétences sont essentielles pour affirmer votre positionnement stratégique de cadre intermédiaire au sein de votre établissement.\n\nBonne continuation dans votre parcours pour valider vos acquis !" }
  ];

  const totalSlides = slides.length;

  const handleNext = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      const next = currentSlide + 1;
      setCurrentSlide(next);
      if (onSlideChange) onSlideChange(next);
    } else {
      if (onComplete) onComplete();
    }
  }, [currentSlide, totalSlides, onComplete, onSlideChange]);

  const handlePrev = useCallback(() => {
    if (currentSlide > 0) {
      const prev = currentSlide - 1;
      setCurrentSlide(prev);
      if (onSlideChange) onSlideChange(prev);
    }
  }, [currentSlide, onSlideChange]);

  const goToSlide = useCallback((idx) => {
    setCurrentSlide(idx);
    if (onSlideChange) onSlideChange(idx);
  }, [onSlideChange]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); handleNext(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); handlePrev(); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleNext, handlePrev]);

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '65vh', textAlign: 'center', padding: '2rem' }}>
            <div style={{ background: primaryColor, color: 'white', padding: '1rem 2.5rem', borderRadius: '1rem', fontSize: '1.1rem', fontWeight: 700, letterSpacing: '0.05em', marginBottom: '2rem' }}>
              {slide.icon} — CAFERUIS
            </div>
            <h1 style={{ fontSize: '2.8rem', fontWeight: 800, color: secondaryColor, marginBottom: '1rem', lineHeight: 1.2 }}>
              {slide.title}
            </h1>
            <p style={{ fontSize: '1.35rem', color: '#1a2e35', maxWidth: '700px', lineHeight: 1.6, marginBottom: '1.5rem' }}>
              {slide.subtitle}
            </p>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '1rem' }}>
              {slide.chapters.split('•').map((c, i) => (
                <span key={i} style={{ background: 'rgba(234,88,12,0.12)', color: primaryColor, padding: '0.5rem 1rem', borderRadius: '2rem', fontSize: '0.9rem', fontWeight: 600 }}>
                  {c.trim()}
                </span>
              ))}
            </div>
          </div>
        );

      case 'sommaire':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '65vh', padding: '2rem' }}>
            <h2 style={{ fontSize: '1.8rem', fontWeight: 700, color: secondaryColor, marginBottom: '2rem', textAlign: 'center' }}>{slide.title}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', width: '100%', maxWidth: '700px' }}>
              {slide.chapters.map((ch, i) => (
                <button key={i} onClick={() => goToSlide(ch.targetSlide)}
                  style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.5rem', background: 'rgba(234,88,12,0.08)', border: '1px solid rgba(234,88,12,0.2)', borderRadius: '0.75rem', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(234,88,12,0.15)'; e.currentTarget.style.borderColor = primaryColor; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(234,88,12,0.08)'; e.currentTarget.style.borderColor = 'rgba(234,88,12,0.2)'; }}
                >
                  <span style={{ background: primaryColor, color: 'white', borderRadius: '50%', width: '2.2rem', height: '2.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1rem', flexShrink: 0 }}>{ch.num}</span>
                  <span style={{ fontSize: '0.95rem', color: '#1a2e35', fontWeight: 500, lineHeight: 1.4 }}>{ch.title}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 'section':
        return (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            minHeight: '65vh', textAlign: 'center', padding: '2rem',
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
            borderRadius: '1.5rem', margin: '1rem', color: 'white'
          }}>
            <h2 style={{ fontSize: '2.2rem', fontWeight: 800, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ fontSize: '1.3rem', fontWeight: 500, opacity: 0.95, maxWidth: '600px', marginBottom: '1rem' }}>{slide.subtitle}</p>
            {slide.description && <p style={{ fontSize: '1rem', opacity: 0.85, maxWidth: '550px', lineHeight: 1.6 }}>{slide.description}</p>}
          </div>
        );

      case 'content':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '65vh', padding: '2rem 3rem', maxWidth: '850px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: secondaryColor, marginBottom: '1.5rem', textAlign: 'center', lineHeight: 1.3 }}>{slide.title}</h2>
            {slide.body && <p style={{ fontSize: '0.95rem', color: '#374151', lineHeight: 1.7, marginBottom: '1rem', textAlign: 'justify', whiteSpace: 'pre-line' }}>{slide.body}</p>}
            {slide.bullets && (
              <ul style={{ listStyle: 'none', padding: 0, width: '100%' }}>
                {slide.bullets.map((b, i) => (
                  <li key={i} style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#374151', lineHeight: 1.5 }}>
                    <span style={{ color: primaryColor, fontWeight: 700, flexShrink: 0 }}>&#9654;</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            {slide.highlight && (
              <div style={{ marginTop: '1rem', padding: '1rem 1.25rem', background: 'rgba(234,88,12,0.08)', borderLeft: `4px solid ${primaryColor}`, borderRadius: '0.5rem', fontSize: '0.9rem', color: '#1a2e35', lineHeight: 1.6, fontStyle: 'italic' }}>
                {slide.highlight}
              </div>
            )}
          </div>
        );

      case 'grid':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '65vh', padding: '2rem' }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: secondaryColor, marginBottom: '1.5rem', textAlign: 'center' }}>{slide.title}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: slide.items.length <= 3 ? 'repeat(auto-fit, minmax(250px, 1fr))' : 'repeat(2, 1fr)', gap: '1rem', width: '100%', maxWidth: '800px' }}>
              {slide.items.map((item, i) => (
                <div key={i} style={{ padding: '1.25rem', background: 'white', borderRadius: '0.75rem', border: '1px solid rgba(234,88,12,0.15)', boxShadow: '0 2px 8px rgba(234,88,12,0.06)' }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: primaryColor, marginBottom: '0.4rem' }}>{item.title}</h3>
                  <p style={{ fontSize: '0.85rem', color: '#4B5563', lineHeight: 1.5 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'congratulations':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '65vh', textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>&#127942;</div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, color: primaryColor, marginBottom: '1rem' }}>{slide.title}</h2>
            <p style={{ fontSize: '1rem', color: '#374151', maxWidth: '600px', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{slide.message}</p>
          </div>
        );

      default:
        return <div style={{ padding: '2rem', textAlign: 'center' }}>Slide non reconnue</div>;
    }
  };

  const slide = slides[currentSlide];

  return (
    <div style={{ position: 'relative', minHeight: '100vh', background: bgGradient, fontFamily: "'Segoe UI', sans-serif" }}>
      {/* Progress bar */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '4px', background: 'rgba(234,88,12,0.15)', zIndex: 50 }}>
        <div style={{ height: '100%', background: `linear-gradient(90deg, ${primaryColor}, ${accentColor})`, width: `${((currentSlide + 1) / totalSlides) * 100}%`, transition: 'width 0.3s ease' }} />
      </div>

      {/* Slide content */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem 1rem', minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '100%' }}>
          {renderSlide(slide)}
        </div>
      </div>

      {/* Navigation */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '1rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,247,237,0.95)', backdropFilter: 'blur(10px)', borderTop: '1px solid rgba(234,88,12,0.1)' }}>
        <button data-testid="bc4-prev-btn" onClick={handlePrev} disabled={currentSlide === 0}
          style={{ padding: '0.6rem 1.5rem', borderRadius: '0.5rem', border: `1px solid ${primaryColor}`, background: 'transparent', color: primaryColor, cursor: currentSlide === 0 ? 'not-allowed' : 'pointer', opacity: currentSlide === 0 ? 0.4 : 1, fontWeight: 600, fontSize: '0.9rem' }}>
          &#8592; Précédent
        </button>

        <span style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: 500 }}>
          {currentSlide + 1} / {totalSlides}
        </span>

        <button data-testid="bc4-next-btn" onClick={handleNext}
          style={{ padding: '0.6rem 1.5rem', borderRadius: '0.5rem', border: 'none', background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`, color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>
          {currentSlide === totalSlides - 1 ? 'Terminer' : 'Suivant &#8594;'}
        </button>
      </div>
    </div>
  );
};

export default CAFBC4Presentation;
